import React, { useState, useEffect } from 'react';
//import { toast } from 'react-toastify';
import toast from '../../utils/toast';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { useFormSubmissions } from './hooks/useFormSubmissions';
import { useBulkActions } from './hooks/useBulkActions';
import Filters from './Filters';
import BulkActions from './BulkActions';
import FormSubmissionsTable from './FormSubmissionsTable';
import ClientDetailsModal from './ClientDetailsModal';
import UserDetailsModal from './UserDetailsModal';
import DocumentUpload from '../DocumentUpload';
import VerificationForm from '../VerificationForm';
import FormSubmissionHistory from '../FormSubmissionHistory';
import FormSubmissionDetails from '../FormSubmissionDetails';
import FormSubmissionEdit from '../FormSubmissionEdit/FormSubmissionEdit';
import 'react-toastify/dist/ReactToastify.css';

const FormSubmissions = () => {
  const { checkUpdatePermission, checkDeletePermission } = useAuth();
  const navigate = useNavigate();
  const [formSubmissionStats, setFormSubmissionStats] = useState({ statusCounts: {}, deadlineCounts: {} });

  const {
    // State
    formSubmissions,
    filteredSubmissions,
    loading,
    error,
    selectedSubmission,
    showDocumentUpload,
    showVerification,
    showHistory,
    showDetails,
    showEdit,
    selectedSubmissions,
    selectAll,
    showClientDetails,
    showUserDetails,
    selectedClient,
    selectedUser,
    filters,
    showFilters,
    
    // Actions
    setSelectedSubmission,
    setShowDocumentUpload,
    setShowVerification,
    setShowHistory,
    setShowDetails,
    setShowEdit,
    setShowClientDetails,
    setShowUserDetails,
    setSelectedClient,
    setSelectedUser,
    setShowFilters,
    setFormSubmissions: updateFormSubmissions,
    
    // Handlers
    handleSelectSubmission,
    handleSelectAll,
    clearSelection,
    handleFilterChange,
    clearFilters,
    getActiveFiltersCount,
    getActiveFiltersSummary,
    fetchFormSubmissions,
    
    // Utility functions
    calculatePendingTime,
    getPendingTimeColor
  } = useFormSubmissions();

  const {
    handleBulkViewDetails,
    handleBulkEdit,
    handleBulkSendEmail,
    handleBulkViewHistory,
    handleBulkDocumentUpload,
    handleBulkVerification,
    handleBulkCreateInvoice,
    handleBulkDelete
  } = useBulkActions(filteredSubmissions, selectedSubmissions);

  const fetchFormSubmissionStats = async () => {
    try {
      const res = await api.get('/form-submissions/stats');
      if (res.data) {
        setFormSubmissionStats({
          statusCounts: res.data.statusCounts || {},
          deadlineCounts: res.data.deadlineCounts || {}
        });
      }
    } catch (e) {
      console.error('Error fetching form submission stats:', e);
    }
  };

  useEffect(() => {
    fetchFormSubmissionStats();
  }, []);

  // Status icon function
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  // Event handlers
  const handleDocumentUpload = (submission) => {
    setSelectedSubmission(submission);
    setShowDocumentUpload(true);
  };

  const handleVerification = (submission) => {
    setSelectedSubmission(submission);
    setShowVerification(true);
  };

  const handleUploadComplete = () => {
    setShowDocumentUpload(false);
    setSelectedSubmission(null);
    fetchFormSubmissions();
    fetchFormSubmissionStats();
    toast.success('Documents uploaded successfully');
  };

  const handleVerificationComplete = () => {
    setShowVerification(false);
    setSelectedSubmission(null);
    fetchFormSubmissions();
    fetchFormSubmissionStats();
    toast.success('Verification completed successfully');
  };

  const handleShowHistory = (submission) => {
    setSelectedSubmission(submission);
    setShowHistory(true);
  };

  const handleViewDetails = (submission) => {
    setSelectedSubmission(submission);
    setShowDetails(true);
  };

  const handleEditSubmission = (submission) => {
    setSelectedSubmission(submission);
    setShowEdit(true);
  };

  const handleEditComplete = (updatedSubmission) => {
    setShowEdit(false);
    setSelectedSubmission(null);
    
    // Only update if we have valid updated submission data
    if (updatedSubmission && updatedSubmission._id) {
      console.log('=== FRONTEND: Received updated submission:', updatedSubmission);
      console.log('=== FRONTEND: Document progress:', updatedSubmission.documentProgress);
      console.log('=== FRONTEND: Verification progress:', updatedSubmission.verificationProgress);
      
      // Process the updated submission data to match the expected format
      const processedSubmission = {
        ...updatedSubmission,
        form: updatedSubmission.formId || { name: 'Unnamed Form', description: '' },
        submittedBy: updatedSubmission.userId || { name: 'Unknown', email: '' },
        // Use the actual calculated progress from the backend response
        documentProgress: updatedSubmission.documentProgress || { uploaded: 0, total: 0 },
        verificationProgress: updatedSubmission.verificationProgress || { completed: 0, total: 0 },
        productsData: updatedSubmission.productsData || []
      };
      
      console.log('=== FRONTEND: Processed submission:', processedSubmission);
      
      // Update the submission in the list
      updateFormSubmissions(prev => {
        const updated = prev.map(sub => 
          sub._id === processedSubmission._id ? processedSubmission : sub
        );
        console.log('=== FRONTEND: Updated submissions list:', updated);
        return updated;
      });
      toast.success('Form submission updated successfully');
      fetchFormSubmissionStats();
    } else {
      // If no updated data, just refresh the list
      console.log('=== FRONTEND: No updated data, refreshing from server');
      fetchFormSubmissions();
      fetchFormSubmissionStats();
    }
  };

  const handleFormNameClick = (submission) => {
    console.log(checkUpdatePermission('Form Submissions'));
    if (checkUpdatePermission('Form Submissions')) {
      handleEditSubmission(submission);
    } else {
      handleViewDetails(submission);
    }
  };

  const handleCreateProformaInvoice = async (submission) => {
    try {
      const response = await api.post(`/form-submissions/${submission._id}/proforma-invoice`);
      toast.success('Proforma invoice created successfully');

      // Refresh the submissions list to show the new invoice
      fetchFormSubmissions();
      fetchFormSubmissionStats();
    } catch (error) {
      console.error('Error creating proforma invoice:', error);

      if (error.response?.status === 400) {
        toast.error(error.response.data.message || 'Failed to create proforma invoice');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to create proforma invoice. Please try again.');
      }
    }
  };

  const handleSendEmail = async (submission) => {
    try {
      const response = await api.post(`/form-submissions/${submission._id}/send-email`);
      toast.success(`Email sent successfully to ${response.data.recipientEmail}`);
    } catch (error) {
      console.error('Error sending email:', error);
      
      if (error.response?.status === 400) {
        toast.error(error.response.data.message || 'Failed to send email');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to send email. Please try again.');
      }
    }
  };

  // Delete handlers
  const handleDelete = async (submission) => {
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete this form submission? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/form-submissions/${submission._id}`);
      toast.success('Form submission deleted successfully');
      
      // Refresh the submissions list
      fetchFormSubmissions();
    } catch (error) {
      console.error('Error deleting form submission:', error);
      
      if (error.response?.status === 403) {
        toast.error('You do not have permission to delete form submissions');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to delete form submission. Please try again.');
      }
    }
  };

  const handleBulkDeleteAction = () => {
    handleBulkDelete(fetchFormSubmissions);
  };

  // Client and User detail handlers
  const handleClientClick = async (clientData) => {
    console.log('Client data received:', clientData);
    try {
      // If we have a masterDataId, fetch the full master data
      if (clientData?._id) {
        console.log('Fetching master data for ID:', clientData._id);
        const response = await api.get(`/master-data/${clientData._id}`);
        console.log('API response:', response.data);
        setSelectedClient(response.data);
      } else {
        console.log('No ID found, using basic data');
        // Fallback to the basic data if no ID
        setSelectedClient(clientData);
      }
      setShowClientDetails(true);
    } catch (error) {
      console.error('Error fetching client details:', error);
      // Fallback to the basic data if API call fails
      setSelectedClient(clientData);
      setShowClientDetails(true);
    }
  };

  const handleUserClick = async (userData) => {
    try {
      // If we have a userId, fetch the full user data
      if (userData?._id) {
        const response = await api.get(`/users/${userData._id}`);
        setSelectedUser(response.data.data);
      } else {
        // Fallback to the basic data if no ID
        setSelectedUser(userData);
      }
      setShowUserDetails(true);
    } catch (error) {
      console.error('Error fetching user details:', error);
      // Fallback to the basic data if API call fails
      setSelectedUser(userData);
      setShowUserDetails(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600 text-center">
          <p className="text-xl font-semibold mb-2">Error</p>
          <p>{error}</p>
          <button
            onClick={fetchFormSubmissions}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Form Submissions</h1>
          <button
            onClick={() => navigate('/checklist')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ShieldCheckIcon className="h-5 w-5 mr-2" />
            Checklist
          </button>
        </div>

        {/* Dashboard: Status + Deadline counts */}
        <div className="bg-white rounded-lg shadow p-4 mb-6 overflow-hidden">
          <div className="flex items-center gap-6 overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-sm font-semibold text-gray-700">Status:</span>
              <div className="flex flex-wrap items-center gap-2">
                {formSubmissionStats.statusCounts && Object.entries(formSubmissionStats.statusCounts).length > 0 ? (
                  Object.entries(formSubmissionStats.statusCounts).map(([status, count]) => (
                    <span key={status} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                      {status.replace(/_/g, ' ')}: <strong className="ml-1">{count}</strong>
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-gray-500">—</span>
                )}
              </div>
            </div>
            <span className="text-gray-300 shrink-0">|</span>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-sm font-semibold text-gray-700">Deadline:</span>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-50 text-red-800">
                  Overdue: <strong className="ml-1">{formSubmissionStats.deadlineCounts?.overdue ?? 0}</strong>
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-amber-50 text-amber-800">
                  Today: <strong className="ml-1">{formSubmissionStats.deadlineCounts?.today ?? 0}</strong>
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-50 text-blue-800">
                  Next 7 days: <strong className="ml-1">{formSubmissionStats.deadlineCounts?.next7 ?? 0}</strong>
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                  Next 30 days: <strong className="ml-1">{formSubmissionStats.deadlineCounts?.next30 ?? 0}</strong>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <Filters
          filters={filters}
          showFilters={showFilters}
          onFilterChange={handleFilterChange}
          onToggleFilters={() => setShowFilters(!showFilters)}
          onClearFilters={clearFilters}
          activeFiltersCount={getActiveFiltersCount()}
          activeFiltersSummary={getActiveFiltersSummary()}
        />

        {/* Bulk Actions Section */}
        <BulkActions
          selectedCount={selectedSubmissions.size}
          onClearSelection={clearSelection}
          onViewDetails={() => handleBulkViewDetails(handleViewDetails)}
          onEdit={() => handleBulkEdit(handleEditSubmission)}
          onSendEmail={() => handleBulkSendEmail(handleSendEmail)}
          onViewHistory={() => handleBulkViewHistory(handleShowHistory)}
          onDocumentUpload={() => handleBulkDocumentUpload(handleDocumentUpload)}
          onVerification={() => handleBulkVerification(handleVerification)}
          onCreateInvoice={() => handleBulkCreateInvoice(handleCreateProformaInvoice)}
          onDelete={handleBulkDeleteAction}
          hasUpdatePermission={checkUpdatePermission('Form Submissions')}
          hasDeletePermission={checkDeletePermission('Form Submissions')}
        />

        {filteredSubmissions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">
              {formSubmissions.length === 0 ? 'No form submissions found' : 'No submissions match your filters'}
            </p>
            {formSubmissions.length > 0 && (
              <button
                onClick={clearFilters}
                className="mt-2 px-4 py-2 text-sm text-blue-600 hover:text-blue-800"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <FormSubmissionsTable
            filteredSubmissions={filteredSubmissions}
            selectedSubmissions={selectedSubmissions}
            onSelectSubmission={handleSelectSubmission}
            onSelectAll={handleSelectAll}
            selectAll={selectAll}
            onFormNameClick={handleFormNameClick}
            onClientClick={handleClientClick}
            onUserClick={handleUserClick}
            onDocumentUpload={handleDocumentUpload}
            onVerification={handleVerification}
            onCreateProformaInvoice={handleCreateProformaInvoice}
            onDelete={handleDelete}
            getStatusIcon={getStatusIcon}
            getPendingTimeColor={getPendingTimeColor}
            calculatePendingTime={calculatePendingTime}
            checkUpdatePermission={checkUpdatePermission}
            checkDeletePermission={checkDeletePermission}
          />
        )}

        {/* Modals */}
        <DocumentUpload
          formSubmissionId={selectedSubmission?._id}
          allProducts={selectedSubmission?.productsData || []}
          onUploadComplete={handleUploadComplete}
          isOpen={showDocumentUpload}
          onClose={() => {
            setShowDocumentUpload(false);
            setSelectedSubmission(null);
          }}
        />

        <VerificationForm
          formSubmissionId={selectedSubmission?._id}
          products={selectedSubmission?.productsData || []}
          onVerificationComplete={handleVerificationComplete}
          isOpen={showVerification}
          onClose={() => {
            setShowVerification(false);
            setSelectedSubmission(null);
          }}
        />

        <FormSubmissionHistory
          formSubmissionId={selectedSubmission?._id}
          isOpen={showHistory}
          onClose={() => {
            setShowHistory(false);
            setSelectedSubmission(null);
          }}
        />

        {selectedSubmission && showDetails && (
          <FormSubmissionDetails
            isOpen={showDetails}
            submission={selectedSubmission}
            onClose={() => {
              setSelectedSubmission(null);
              setShowDetails(false);
            }}
          />
        )}

        {selectedSubmission && showEdit && (
          <FormSubmissionEdit
            isOpen={showEdit}
            submission={selectedSubmission}
            onUpdate={handleEditComplete}
            onClose={() => {
              setSelectedSubmission(null);
              setShowEdit(false);
            }}
          />
        )}

        <ClientDetailsModal
          isOpen={showClientDetails}
          client={selectedClient}
          onClose={() => {
            setShowClientDetails(false);
            setSelectedClient(null);
          }}
        />

        <UserDetailsModal
          isOpen={showUserDetails}
          user={selectedUser}
          onClose={() => {
            setShowUserDetails(false);
            setSelectedUser(null);
          }}
        />
      </div>
    </>
  );
};

export default FormSubmissions; 